import React, { useState, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
  ScrollView,
  Dimensions,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Button } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import { useTranslation } from "../../../utils/useTranslation";
import {
  getChinaTrackerShipmentDetailsByDetailId,
  updateFactoryRunShipment,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { DatePickerModal } from "react-native-paper-dates";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import moment from "moment";


interface FactoryRunShipmentModalProps {
  visible: boolean;
  onClose: () => void;
  shipmentRow: any;
  factoryOrderDetail: any;
}

interface FormData {
  shipmentQty: string;
  containerNo: string;
  shipmentNo: string;
  ETD: Date;
  duration: string;
  ETA?: Date;
  remarks?: string;
}

const ChinaTrackerShipmentModalUpdate: React.FC<
  FactoryRunShipmentModalProps
> = ({ visible, onClose, shipmentRow, factoryOrderDetail }) => {
  const dispatch = useDispatch<any>();
  const toast = useToast();
  const { t } = useTranslation();
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);
  const { loadingShipmentsAction } = useSelector(
    (state: any) => state.chinaTracker,
  );

  const [shipmentQtyWarnMsg, setShipmentQtyWarnMsg] = useState<string | null>(
    null,
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      shipmentQty: shipmentRow.shipmentQty?.toString() ?? "",
      containerNo: shipmentRow.containerNo ?? "",
      shipmentNo: shipmentRow.shipmentNo?.toString() ?? "",
      ETD: shipmentRow.ETD ? new Date(shipmentRow.ETD) : new Date(),
      duration: shipmentRow.duration?.toString() ?? "",
      ETA: shipmentRow.ETA ? new Date(shipmentRow.ETA) : undefined,
      remarks: shipmentRow.remarks ?? "",
    },
  });

  // Auto-update ETA when ETD or duration changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "duration" || name === "ETD") {
        const etd = value.ETD;
        const duration = Number(value.duration);
        if (etd instanceof Date && duration > 0) {
          const eta = new Date(etd);
          eta.setDate(etd.getDate() + duration);
          setValue("ETA", eta);
        } else {
          setValue("ETA", undefined);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      const etd = data.ETD;
      const duration = Number(data.duration);
      const eta = new Date(etd);
      eta.setDate(etd.getDate() + duration);

      const payload = {
        shipmentNo: data.shipmentNo,
        shipmentQty: Number(data.shipmentQty.toString().replace(/,/g, "")),
        containerNo: data.containerNo,
        ETD: data.ETD,
        duration,
        ETA: eta,
        remarks: data.remarks || "",
        factoryOrderDetail: factoryOrderDetail._id,
      };

      await dispatch(
        updateFactoryRunShipment({ payload: payload, _id: shipmentRow._id }),
      ).unwrap();
      toast.show("Shipment updated successfully", { type: "success" });
      onClose();
      dispatch(
        getChinaTrackerShipmentDetailsByDetailId(factoryOrderDetail._id),
      ).unwrap();
    } catch (err: any) {
      console.error("Error updating shipment:", err);
      toast.show(`Failed to update shipment: ${err}`, { type: "danger" });
    }
  };

  const renderDatePicker = (
    label: string,
    value: Date | undefined,
    onChange: (date: Date) => void,
    disabled?: boolean,
  ) => {
    const [open, setOpen] = useState(false);
    return (
      <View style={styles.field}>
        <Text style={styles.label}>{label}</Text>
        <TouchableOpacity
          disabled={disabled}
          onPress={() => setOpen(true)}
          style={[
            styles.input,
            {
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: disabled
                ? themeColors.surfaceSubtle
                : themeColors.surface,
            },
          ]}
        >
          <Text
            style={{
              color: value ? themeColors.text : themeColors.textSecondary,
              fontSize: isSmallScreen ? 13 : 14,
            }}
          >
            {value ? moment(value).format("DD/MM/YYYY") : "DD/MM/YYYY"}
          </Text>
          <MaterialCommunityIcons
            name="calendar"
            size={20}
            color={themeColors.text}
          />
        </TouchableOpacity>
        <DatePickerModal
          locale="en-GB"
          mode="single"
          visible={open}
          onDismiss={() => setOpen(false)}
          date={value instanceof Date ? value : undefined}
          onConfirm={(params: any) => {
            setOpen(false);
            if (params.date && !disabled) onChange(params.date);
          }}
        />
      </View>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {t("updateShipment")} - {factoryOrderDetail?.orderNumber}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Shipment No (disabled) */}
            <Controller
              control={control}
              name="shipmentNo"
              render={({ field: { value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("shipmentNo")}</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: themeColors.surfaceSubtle }]}
                    value={value}
                    editable={false}
                  />
                </View>
              )}
            />

            {/* Shipment Quantity */}
            <Controller
              control={control}
              name="shipmentQty"
              rules={{
                required: "Shipment quantity is required",
                validate: (value: any) => {
                  if (!value) return "Quantity is required";
                  if (value <= 0) return "Quantity must be greater than 0";
                  return true;
                },
              }}
              render={({ field: { onChange, value } }) => {
                const handleNumberChange = (text: string) => {
                  const numericText = text.replace(/[^0-9]/g, "");
                  onChange(numericText === "" ? "" : Number(numericText));
                };
                return (
                  <View style={styles.field}>
                    <Text style={styles.label}>{t("shipmentQty")}</Text>
                    <TextInput
                      keyboardType="numeric"
                      style={styles.input}
                      value={value?.toLocaleString() || ""}
                      onChangeText={handleNumberChange}
                      placeholder="Enter shipment quantity"
                      placeholderTextColor={themeColors.textSecondary}
                    />
                    {errors.shipmentQty && (
                      <Text style={styles.error}>
                        {errors.shipmentQty.message}
                      </Text>
                    )}
                  </View>
                );
              }}
            />

            {/* Container Number */}
            <Controller
              control={control}
              name="containerNo"
              rules={{ required: "Container number is required" }}
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("containerNo")}</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Enter container number"
                    placeholderTextColor={themeColors.textSecondary}
                    value={value}
                    onChangeText={onChange}
                  />
                  {errors.containerNo && (
                    <Text style={styles.error}>
                      {errors.containerNo.message}
                    </Text>
                  )}
                </View>
              )}
            />

            {/* ETD */}
            <Controller
              control={control}
              name="ETD"
              rules={{ required: "ETD is required" }}
              render={({ field: { onChange, value } }) =>
                renderDatePicker("ETD", value, onChange)
              }
            />

            {/* Duration */}
            <Controller
              control={control}
              name="duration"
              rules={{ required: "Duration is required" }}
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("duration")}</Text>
                  <TextInput
                    keyboardType="numeric"
                    style={styles.input}
                    placeholder="Enter duration days"
                    placeholderTextColor={themeColors.textSecondary}
                    value={value}
                    onChangeText={onChange}
                  />
                  {errors.duration && (
                    <Text style={styles.error}>{errors.duration.message}</Text>
                  )}
                </View>
              )}
            />

            {/* ETA (disabled) */}
            <Controller
              control={control}
              name="ETA"
              render={({ field: { onChange, value } }) =>
                renderDatePicker("ETA", value, onChange, true)
              }
            />

            {/* Remarks */}
            <Controller
              control={control}
              name="remarks"
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("remarks")}</Text>
                  <TextInput
                    multiline
                    numberOfLines={3}
                    style={[styles.input, { height: 80 }]}
                    placeholder="Enter remarks (optional)"
                    placeholderTextColor={themeColors.textSecondary}
                    value={value}
                    onChangeText={onChange}
                  />
                </View>
              )}
            />

            <Button
              mode="contained"
              onPress={handleSubmit(onSubmit)}
              loading={loadingShipmentsAction}
              disabled={loadingShipmentsAction}
              style={styles.submitButton}
              labelStyle={{ color: themeColors.surface }}
            >
              {t("updateShipment")}
            </Button>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");
const isSmallScreen = SCREEN_WIDTH < 400;

function createStyles(colors: any) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.25)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: colors.surface,
    padding: isSmallScreen ? 15 : 25,
    width:
      Platform.OS === "web"
        ? Math.min(500, SCREEN_WIDTH * 0.8)
        : SCREEN_WIDTH * 0.95,
    borderRadius: 10,
    maxHeight: SCREEN_HEIGHT * 0.9,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: isSmallScreen ? 8 : 10,
  },
  title: {
    fontSize: isSmallScreen ? 16 : 18,
    fontWeight: "600",
    color: colors.text,
  },
  closeButton: {
    backgroundColor: "#1E88E5",
    borderRadius: 16,
    paddingHorizontal: isSmallScreen ? 8 : 10,
    paddingVertical: isSmallScreen ? 2 : 4,
  },
  closeButtonText: {
    color: colors.surface,
    fontSize: isSmallScreen ? 14 : 16,
  },
  field: {
    marginBottom: isSmallScreen ? 10 : 12,
  },
  label: {
    fontWeight: "bold",
    marginBottom: 4,
    fontSize: isSmallScreen ? 13 : 14,
    color: colors.text,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: isSmallScreen ? 8 : 10,
    fontSize: isSmallScreen ? 13 : 14,
    color: colors.text,
    backgroundColor: colors.surface,
  },
  error: {
    color: "red",
    fontSize: isSmallScreen ? 11 : 12,
    marginTop: 2,
  },
  submitButton: {
    marginTop: 20,
    backgroundColor: colors.text,
    borderRadius: 8,
    paddingVertical: isSmallScreen ? 10 : 12,
  },
  datePicker: {
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
});
}

export default ChinaTrackerShipmentModalUpdate;
